import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Form, FormControl, FormDialog, FormField, FormItem, FormLabel,
	Input,
} from '@rentflowdev/ui'
import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useUpdateInvoiceMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import type { Invoice } from '@/types'

const schema = z.object({
	title:   z.string().trim().min(1, 'finances.editDialog.errors.titleRequired'),
	amount:  z.coerce.number().positive('finances.editDialog.errors.amountPositive'),
	dueDate: z.string().optional(),
})

type EditFormValues = z.infer<typeof schema>

interface EditInvoiceDialogProps {
	open:         boolean
	onOpenChange: (open: boolean) => void
	invoice:      Invoice | null
}

function isoDay(iso?: string): string {
	return iso ? iso.slice(0, 10) : ''
}

/**
 * Lightweight correction dialog — only exposes fields the backend
 * `UpdateInvoiceBody` actually mutates (title / amount / dueDate). All other
 * invoice attributes (rental, type, period, items) are bound at creation
 * time and cannot be changed without re-issuing the invoice.
 */
export function EditInvoiceDialog({ open, onOpenChange, invoice }: EditInvoiceDialogProps) {
	const { t } = useTranslation()
	const [updateInvoice, { isLoading }] = useUpdateInvoiceMutation()
	const [hasError, setHasError] = useState(false)
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

	const form = useForm<EditFormValues>({
		resolver: zodResolver(schema) as Resolver<EditFormValues>,
		defaultValues: {
			title:   invoice?.title ?? '',
			amount:  invoice ? Number(invoice.amount) : 0,
			dueDate: isoDay(invoice?.dueDate),
		},
		mode: 'onChange',
	})

	useEffect(() => {
		if (open && invoice) {
			form.reset({
				title:   invoice.title,
				amount:  Number(invoice.amount),
				dueDate: isoDay(invoice.dueDate),
			})
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setHasError(false)
			setErrorMsg(undefined)
		}
	}, [open, invoice, form])

	async function onSubmit(values: EditFormValues) {
		if (!invoice) return
		setHasError(false)
		setErrorMsg(undefined)
		try {
			await updateInvoice({
				id: invoice.id,
				body: {
					title:   values.title.trim(),
					amount:  String(values.amount),
					dueDate: values.dueDate || undefined,
				},
			}).unwrap()
			onOpenChange(false)
		} catch (err) {
			setHasError(true)
			setErrorMsg(extractErrorMessage(err))
		}
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={onOpenChange}
				title={t('finances.editDialog.title')}
				onSubmit={form.handleSubmit(onSubmit)}
				submitLabel={t('common.save')}
				cancelLabel={t('common.cancel')}
				closeAriaLabel={t('common.close')}
				isSubmitting={isLoading}
				isSubmitDisabled={!form.formState.isValid}
				contentClassName="sm:max-w-md"
			>
				{hasError && (
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertTitle>{t('finances.editDialog.errors.save')}</AlertTitle>
						{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
					</Alert>
				)}

				<FormField
					control={form.control}
					name="title"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('finances.editDialog.fields.title')}</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
						</FormItem>
					)}
				/>

				<div className="grid grid-cols-2 gap-3">
					<FormField
						control={form.control}
						name="amount"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('finances.editDialog.fields.amount')}</FormLabel>
								<FormControl>
									<Input type="number" min={0} step="0.01" {...field} value={field.value ?? ''} />
								</FormControl>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="dueDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('finances.editDialog.fields.dueDate')}</FormLabel>
								<FormControl>
									<Input type="date" {...field} value={field.value ?? ''} />
								</FormControl>
							</FormItem>
						)}
					/>
				</div>

				<p className="text-xs text-(--color-muted-foreground)">
					{t('finances.editDialog.lockedHint')}
				</p>
			</FormDialog>
		</Form>
	)
}
